import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Flame,
  ShieldCheck,
  Truck,
  RotateCcw,
  BadgeCheck,
  Smartphone,
  ShoppingBag,
  UtensilsCrossed,
  Shirt,
  Monitor,
  Dumbbell,
  Home as HomeIcon,
  Gift,
  Grid,
  ChevronRight,
  Heart
} from 'lucide-react';
import { productsAPI, categoriesAPI } from '../services/api';
import ProductCard from '../components/ProductCard';
import HeroSection from '../components/HeroSection';
import SaleProductsScroll from '../components/SaleProductsScroll';
import SEO from '../components/SEO'; // ADD THIS
import './css/Home.css';

// Placeholder images for promos/brands - replace with production assets
const PROMO_BANNERS = [
  {
    title: 'Promos mega du week-end',
    description: "Electronique, TV et gaming jusqu'a -50%.",
    tag: 'Banniere de demo',
    image: 'https://via.placeholder.com/720x360?text=Promo+Placeholder+1',
    link: '/category/electronics'
  },
  {
    title: 'Nouveau style',
    description: 'Mode du quotidien et accessoires a prix doux.',
    tag: 'Banniere de demo',
    image: 'https://via.placeholder.com/720x360?text=Promo+Placeholder+2',
    link: '/category/fashion'
  }
];

const POPULAR_BRANDS = [
  { name: 'TechNova', image: 'https://via.placeholder.com/160x64?text=Brand+Logo' },
  { name: 'UrbanWear', image: 'https://via.placeholder.com/160x64?text=Brand+Logo' },
  { name: 'HomeCraft', image: 'https://via.placeholder.com/160x64?text=Brand+Logo' },
  { name: 'FreshMart', image: 'https://via.placeholder.com/160x64?text=Brand+Logo' },
  { name: 'FitPro', image: 'https://via.placeholder.com/160x64?text=Brand+Logo' },
  { name: 'BeautyLab', image: 'https://via.placeholder.com/160x64?text=Brand+Logo' }
];

const VALUE_PROPS = [
  { icon: Truck, title: 'Livraison express', description: 'Expedition le jour meme sur les essentiels, avec suivi en temps reel.' },
  { icon: ShieldCheck, title: 'Paiement securise', description: 'Paiement protege avec des options locales et globales de confiance.' },
  { icon: RotateCcw, title: 'Retours faciles', description: 'Retours simples sous 7 a 14 jours sur les articles eligibles.' },
  { icon: BadgeCheck, title: 'Qualite verifiee', description: 'Vendeurs verifies, produits authentiques, service tres bien note.' }
];

const getCategoryIcon = (name = '') => {
  const key = name.toLowerCase();
  if (key.includes('phone') || key.includes('tech') || key.includes('electronics')) return Smartphone;
  if (key.includes('computer') || key.includes('laptop')) return Monitor;
  if (key.includes('fashion') || key.includes('clothing') || key.includes('wear')) return Shirt;
  if (key.includes('home') || key.includes('furniture')) return HomeIcon;
  if (key.includes('sport') || key.includes('fitness')) return Dumbbell;
  if (key.includes('grocery') || key.includes('food')) return UtensilsCrossed;
  if (key.includes('beauty') || key.includes('care')) return Sparkles;
  if (key.includes('gift') || key.includes('toy')) return Gift;
  if (key.includes('accessories')) return ShoppingBag;
  return Grid;
};

const Home = ({ openCart }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsResponse, categoriesResponse] = await Promise.all([
          productsAPI.getAll(),
          categoriesAPI.getAll(),
        ]);
        setProducts(productsResponse.data);
        setCategories(categoriesResponse.data);
      } catch (err) {
        console.error('Failed to fetch data', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const featuredCategories = useMemo(() => categories.slice(0, 8), [categories]);

  const flashDeals = useMemo(
    () => products.filter((p) => p.salePrice && p.salePrice < p.regularPrice).slice(0, 8),
    [products]
  );

  const trendingProducts = useMemo(() => {
    if (!products.length) return [];
    const scored = products.map((product) => {
      const ratingScore = (product.averageRating || 0) * 10;
      const reviewsScore = (product.totalReviews || 0) * 2;
      const saleBoost = product.salePrice ? 8 : 0;
      return { product, score: ratingScore + reviewsScore + saleBoost };
    });
    return scored
      .sort((a, b) => b.score - a.score)
      .map((item) => item.product)
      .slice(0, 8);
  }, [products]);

  const bestSellers = useMemo(() => {
    if (!products.length) return [];
    const scored = products.map((product) => {
      const sold = product.soldCount || product.totalSales || product.ordersCount || 0;
      const reviews = product.totalReviews || 0;
      const fallback = product.stock ? product.stock / 10 : 0;
      return { product, score: sold * 3 + reviews * 2 + fallback };
    });
    return scored
      .sort((a, b) => b.score - a.score)
      .map((item) => item.product)
      .slice(0, 6);
  }, [products]);

  const recommendedProducts = useMemo(() => {
    if (!products.length) return [];
    const pool = [...products].sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
    return pool.slice(0, 12).sort(() => 0.5 - Math.random()).slice(0, 6);
  }, [products]);

  const inspirationProducts = useMemo(() => {
    if (!products.length) return [];
    return [...products].slice(-8).reverse();
  }, [products]);

  if (loading) return <div className="home-loading">Chargement...</div>;

  return (
    <>
      {/* ADD SEO COMPONENT */}
      <SEO
        title="Reyza - Votre boutique en ligne tout-en-un"
        description="Achetez les derniers produits aux meilleurs prix. Livraison gratuite des 50 DT. Paiement securise et livraison rapide."
        keywords="shopping en ligne, e-commerce, meilleures offres, livraison gratuite, acheter en ligne"
        url="/"
        includeOrganization
        breadcrumbs={[
          { name: 'Accueil', url: '/' }
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Reyza",
          "url": process.env.REACT_APP_SITE_URL || "http://localhost:3000",
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${process.env.REACT_APP_SITE_URL || "http://localhost:3000"}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        }}
      />

      <div className="home-container">
        {/* Hero Section */}
        <HeroSection />

        {/* Trust & Value Props */}
        <section className="home-section">
          <div className="home-section-inner">
            <div className="home-section-header">
              <div>
                <p className="home-eyebrow">Pourquoi acheter chez Reyza</p>
                <h2 className="home-heading">Marketplace premium, concue pour la rapidite</h2>
                <p className="home-section-subtitle">
                  Tout ce dont vous avez besoin en un seul endroit : electronique, mode, epicerie et plus, avec un paiement rapide et securise.
                </p>
              </div>
              <Link to="/shop-ai" className="home-cta-link home-cta-link-ai">
                <Sparkles size={16} />
                Acheter avec l'IA
                <ChevronRight size={16} />
              </Link>
            </div>
            <div className="home-value-grid">
              {VALUE_PROPS.map((item) => {
                const Icon = item.icon;
                return (
                  <div className="home-value-card" key={item.title}>
                    <div className="home-value-icon">
                      <Icon size={18} />
                    </div>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Categories - Icon Grid */}
        <section className="home-section">
          <div className="home-section-inner">
            <div className="home-section-header">
              <div>
                <p className="home-eyebrow">Categories a la une</p>
                <h2 className="home-heading">Achetez l'essentiel</h2>
                <p className="home-section-subtitle">
                  Decouvrez nos categories les plus appreciees : tech, maison, epicerie et style.
                </p>
              </div>
              <Link to="/categories" className="home-cta-link">
                Voir tout
                <ChevronRight size={16} />
              </Link>
            </div>
            <div className="home-featured-grid">
              {featuredCategories.length > 0 ? (
                featuredCategories.map((category) => {
                  const Icon = getCategoryIcon(category.name);
                  return (
                    <Link
                      key={category.id}
                      to={`/category/${category.slug || category.id}`}
                      className="home-featured-card"
                    >
                      <div className="home-featured-icon">
                        <Icon size={18} />
                      </div>
                      <div className="home-featured-content">
                        <h3>{category.name}</h3>
                        <p>{category.description || 'Selections a decouvrir maintenant.'}</p>
                      </div>
                      <div className="home-featured-image">
                        <img
                          src={category.imageUrl || 'https://via.placeholder.com/200?text=Category'}
                          alt={category.name}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    </Link>
                  );
                })
              ) : (
                  <p className="home-no-categories">Aucune categorie disponible.</p>
              )}
            </div>

            
          </div>
        </section>

        {/* Popular Brands */}
        <section className="home-section">
          <div className="home-section-inner">
            <div className="home-section-header">
              <div>
                <p className="home-eyebrow">Marques populaires</p>
                <h2 className="home-heading">Des marques reconnues, selectionnees pour vous</h2>
              </div>
            </div>
            <div className="home-brands-row">
              {POPULAR_BRANDS.map((brand) => (
                <div className="home-brand-chip" key={brand.name}>
                  <img
                    src={brand.image}
                    alt={`Logo ${brand.name}`}
                    loading="lazy"
                    decoding="async"
                  />
                  <span>{brand.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Flash Deals - Only Sale Products */}
        <SaleProductsScroll openCart={openCart} products={flashDeals.length ? flashDeals : products} />

        {/* Trending & Best Sellers */}
        <section className="home-section">
          <div className="home-section-inner">
            <div className="home-section-header">
              <div>
                <p className="home-eyebrow">Tendances</p>
                <h2 className="home-heading">Meilleures ventes sur la marketplace</h2>
              </div>
              <Link to="/search" className="home-cta-link">
                Voir tous les produits
                <ChevronRight size={16} />
              </Link>
            </div>
            <div className="home-products-grid curated-grid">
              {trendingProducts.length > 0 ? (
                trendingProducts.map((product) => (
                  <ProductCard key={product.id} product={product} openCart={openCart} />
                ))
              ) : (
                  <p className="home-no-products">Aucun produit tendance trouve.</p>
              )}
            </div>

            <div className="home-subheader-row home-best-sellers-header">
              <div className="home-subheader-left">
                <Flame size={16} />
                <span>Coups de coeur de la communaute</span>
              </div>
              <span className="home-subheader-note">Tres bien notes et souvent recommandes</span>
            </div>
            <div className="home-scroll-row">
              {bestSellers.length > 0 ? (
                bestSellers.map((product) => (
                  <div className="home-scroll-card" key={product.id}>
                    <ProductCard product={product} openCart={openCart} />
                  </div>
                ))
              ) : (
                  <p className="home-no-products">Pas encore de meilleures ventes.</p>
              )}
            </div>
          </div>
        </section>

        {/* Promotional Banners */}
        <section className="home-section">
          <div className="home-section-inner">
            <div className="home-section-header">
              <div>
                <p className="home-eyebrow">Selection de saison</p>
                <h2 className="home-heading">Promotions a duree limitee</h2>
              </div>
            </div>
            <div className="home-promo-grid">
              {PROMO_BANNERS.map((banner) => (
                <Link to={banner.link} key={banner.title} className="home-promo-card">
                  <div className="home-promo-text">
                    <span className="home-promo-tag">{banner.tag}</span>
                    <h3>{banner.title}</h3>
                    <p>{banner.description}</p>
                    <span className="home-cta-link">
                      Acheter maintenant
                      <ChevronRight size={14} />
                    </span>
                  </div>
                  <div className="home-promo-image">
                    <img
                      src={banner.image}
                      alt={`Illustration ${banner.title}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended For You */}
        <section className="home-section">
          <div className="home-section-inner">
            <div className="home-section-header">
              <div>
                <p className="home-eyebrow">Personnalise</p>
                <h2 className="home-heading">Recommande pour vous</h2>
                <p className="home-section-subtitle">
                  Base sur ce qui est populaire en ce moment. Ajoutez des articles au panier ou a votre liste.
                </p>
              </div>
            </div>
            <div className="home-products-grid curated-grid">
              {recommendedProducts.length > 0 ? (
                recommendedProducts.map((product) => (
                  <ProductCard key={product.id} product={product} openCart={openCart} />
                ))
              ) : (
                <p className="home-no-products">Aucune recommandation disponible.</p>
              )}
            </div>
          </div>
        </section>

        {/* Inspiration / Recently Viewed */}
        <section className="home-section">
          <div className="home-section-inner">
            <div className="home-section-header">
              <div className="home-subheader-left">
                <Heart size={16} />
                <div>
                  <p className="home-eyebrow">Inspiration</p>
                  <h2 className="home-heading">Continuez a parcourir</h2>
                </div>
              </div>
              <Link to="/search" className="home-cta-link">
                Continuer les achats
                <ChevronRight size={16} />
              </Link>
            </div>
            <div className="home-scroll-row inspiration-row">
              {inspirationProducts.length > 0 ? (
                inspirationProducts.map((product) => (
                  <div className="home-inspiration-card" key={product.id}>
                    <div className="home-inspiration-image">
                      {product.imageUrl ? (
                        <img
                          src={product.imageUrl}
                          alt={product.name}
                          loading="lazy"
                          decoding="async"
                        />
                      ) : (
                        <div className="home-inspiration-placeholder" />
                      )}
                    </div>
                    <div className="home-inspiration-content">
                      <p className="home-inspiration-name">{product.name}</p>
                      <p className="home-inspiration-price">
                        DT {Number(product.salePrice || product.regularPrice || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="home-no-products">Commencez a explorer pour voir l'inspiration.</p>
              )}
            </div>
          </div>
        </section>

        {/* Newsletter / CTA */}
        <section className="home-section">
          <div className="home-section-inner">
            <div className="home-newsletter">
              <div>
                <p className="home-eyebrow">Restez informe</p>
                <h2 className="home-heading">Acces en avant-premiere aux sorties et promos</h2>
                <p className="home-section-subtitle">
                  Inscrivez-vous pour recevoir des selections, nouveautes et coupons exclusifs.
                </p>
                <form className="home-newsletter-form" onSubmit={(e) => e.preventDefault()}>
                  <input type="email" placeholder="Entrez votre email" required />
                  <button type="submit">Me prevenir</button>
                </form>
              </div>
              <div className="home-newsletter-meta">
                <span>Aucun spam.</span>
                <span>Apercus exclusifs.</span>
              </div>
            </div>
          </div>
        </section>

        {/* All Products */}
        <section className="home-products-section">
          <h2 className="home-section-title">Tous les produits</h2>
          <div className="home-products-grid">
            {products.length > 0 ? (
              products.map((product) => (
                <ProductCard key={product.id} product={product} openCart={openCart} />
              ))
            ) : (
              <p className="home-no-products">Aucun produit trouve.</p>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Home;
